import type { ChangeEvent, DragEvent, RefObject } from "react";
import { AlertCircle, ArrowRight, CheckCircle2, FileText, UploadCloud, X } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { formatFileSize } from "../../../../utils/formatFileSize";

interface ResumeUploadStepProps {
  fileInputRef: RefObject<HTMLInputElement>;
  selectedResume: File | null;
  resumeError: string | null;
  isDragging: boolean;
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onDragOver: (event: DragEvent<HTMLDivElement>) => void;
  onDragLeave: (event: DragEvent<HTMLDivElement>) => void;
  onDrop: (event: DragEvent<HTMLDivElement>) => void;
  onRemoveResume: () => void;
  onNext: () => void;
}

export default function ResumeUploadStep({
  fileInputRef,
  selectedResume,
  resumeError,
  isDragging,
  onFileChange,
  onDragOver,
  onDragLeave,
  onDrop,
  onRemoveResume,
  onNext,
}: ResumeUploadStepProps) {
  return (
    <div className="px-6 py-7">
      <div className="mx-auto max-w-2xl">
        <div className="text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue-50">
            <FileText className="h-7 w-7 text-blue-600" />
          </div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Upload Your Resume</h2>
          <p className="mt-2 text-sm leading-6 text-gray-500">
            Upload your latest resume. Your information will be extracted automatically and you can review it before submission.
          </p>
        </div>

        <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={onFileChange} />

        {!selectedResume ? (
          <div
            role="button"
            tabIndex={0}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            className={`mt-7 cursor-pointer rounded-2xl border-2 border-dashed px-6 py-12 text-center transition-colors ${
              isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50/50"
            }`}
          >
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white shadow-sm">
              <UploadCloud className="h-7 w-7 text-blue-600" />
            </div>
            <p className="mt-4 font-semibold text-gray-800">Drag and drop your resume here</p>
            <p className="mt-1 text-sm text-gray-500">or click to browse from your device</p>
            <Button
              type="button"
              variant="outline"
              className="mt-5"
              onClick={(event) => {
                event.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              Select Resume
            </Button>
            <p className="mt-4 text-xs text-gray-400">PDF, DOC or DOCX • Maximum file size 5 MB</p>
          </div>
        ) : (
          <div className="mt-7 rounded-2xl border border-blue-200 bg-blue-50/60 p-5">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-gray-900">{selectedResume.name}</p>
                <p className="mt-1 text-sm text-gray-500">{formatFileSize(selectedResume.size)}</p>
              </div>
              <Button type="button" variant="ghost" size="icon" onClick={onRemoveResume} aria-label="Remove resume">
                <X className="h-5 w-5 text-gray-500" />
              </Button>
            </div>
            <div className="mt-4 flex items-center gap-2 text-sm font-medium text-green-700">
              <CheckCircle2 className="h-4 w-4" />
              Resume is ready for processing
            </div>
          </div>
        )}

        {resumeError && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{resumeError}</span>
          </div>
        )}

        <div className="mt-8 flex justify-end border-t pt-5">
          <Button type="button" onClick={onNext} disabled={!selectedResume}>
            Next
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}